/**
 * Which days of the week, as seven toggles.
 *
 * Shared by the notify step's delivery hours and the schedule trigger — two
 * different questions ("when may this reach someone" and "when should this
 * run") that happen to want the same control. Extracted at the second call
 * site rather than the first.
 *
 * Styled as the filter pills already in this folder (ActivityPage, the template
 * gallery), fixed-width so seven of them read as a week.
 */

/** Sunday-first, matching the stored 0-6 and every calendar the reader has seen. */
const DAYS = [
  { value: 0, short: 'S', label: 'Sunday' },
  { value: 1, short: 'M', label: 'Monday' },
  { value: 2, short: 'T', label: 'Tuesday' },
  { value: 3, short: 'W', label: 'Wednesday' },
  { value: 4, short: 'T', label: 'Thursday' },
  { value: 5, short: 'F', label: 'Friday' },
  { value: 6, short: 'S', label: 'Saturday' },
] as const;

const dayButton = (active: boolean): string =>
  [
    'h-7 w-7 rounded-full border text-xs font-medium transition-colors',
    active
      ? 'border-primary bg-primary text-white'
      : 'border-outline-strong text-ink-muted hover:text-ink',
  ].join(' ');

export const WeekdayPicker = ({
  days,
  onChange,
}: {
  days: number[];
  /** Never called with an empty selection — see below. */
  onChange: (days: number[]) => void;
}): React.ReactElement => (
  <div className="flex gap-1">
    {DAYS.map((d) => {
      const on = days.includes(d.value);
      return (
        <button
          key={d.value}
          type="button"
          aria-label={d.label}
          aria-pressed={on}
          className={dayButton(on)}
          onClick={() => {
            const next = on ? days.filter((x) => x !== d.value) : [...days, d.value].sort();
            // Zero days is a thing that never happens — a message that never
            // sends, or a schedule that never runs. The server rejects it and
            // no author means it, so the last one cannot be turned off.
            if (next.length === 0) return;
            onChange(next);
          }}
        >
          {d.short}
        </button>
      );
    })}
  </div>
);
